import { useState, useEffect, useCallback, useRef } from "react"
import { Helmet } from "react-helmet-async"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Navigation } from "@/components/Navigation"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table"
import { ChevronLeft, ChevronRight, RefreshCw, Loader2, AlertCircle, Zap, Clock, History } from "lucide-react"
import { getScanTypeDisplayName } from "@/utils/displayNames"
import { useReportActions } from "@/hooks/useReportActions"
import { ReportPreview } from "@/components/reports/ReportPreview"
import { ToastContainer } from "@/components/ui/toast"
import { useToast } from "@/hooks/use-toast"
import { ScanProgressTracker } from "@/components/ScanProgressTracker"
import { useAuth } from "@/contexts/AuthContext"
import { useResetTimer } from "@/hooks/useResetTimer"
import { Link } from "react-router-dom"
import { addCSRFHeader } from "@/utils/csrf"
import { fetchWithRefresh } from "@/utils/fetchWithRefresh"

interface Scan {
  id: string
  url: string
  scan_type: string
  status: 'queued' | 'running' | 'completed' | 'failed' | 'partially_completed'
  created_at: string
  completed_at?: string
  security_score?: number
  vulnerabilities_count?: number
  progress?: number
  time_elapsed?: number
  queue_position?: number
  estimated_wait_minutes?: number
}

interface ScanListResponse {
  scans: Scan[]
  total: number
  page: number
  per_page: number
}

interface UrlHistoryItem {
  url: string
  scan_count: number
  last_scanned: string | null
}

export default function ScanManagement() {
  const { toasts, removeToast, toast } = useToast()
  const { user, loading: authLoading, checkAuthStatus } = useAuth()
  const resetTimer = useResetTimer()

  // Scan creation state
  const [targetUrl, setTargetUrl] = useState("")
  const [selectedScans, setSelectedScans] = useState<string[]>([])
  const [isComprehensive, setIsComprehensive] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // URL history state
  const [urlHistory, setUrlHistory] = useState<UrlHistoryItem[]>([])
  const [showUrlDropdown, setShowUrlDropdown] = useState(false)
  const urlDropdownRef = useRef<HTMLDivElement>(null)

  // Scan list state
  const [scans, setScans] = useState<Scan[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalScans, setTotalScans] = useState(0)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [perPage] = useState(10)

  // Track active scans for polling
  const [activeScans, setActiveScans] = useState<Set<string>>(new Set())

  // Shared report actions
  const { selectedReport, reportData, handleViewReport, handleDownloadReport, handleClosePreview } = useReportActions(toast)

  // Store current values in refs to avoid recreating callbacks
  const currentPageRef = useRef(currentPage)
  const statusFilterRef = useRef(statusFilter)

  useEffect(() => {
    currentPageRef.current = currentPage
    statusFilterRef.current = statusFilter
  }, [currentPage, statusFilter])

  // Helper function to get the correct scan type for each tool
  const getScanTypeForTool = (scanId: string): string => {
    const scanTypeMapping: Record<string, string> = {
      'owasp-zap': 'quick',           // ZAP uses 'quick' for baseline scans
      'nuclei': 'full',               // Nuclei uses 'full' scans
      'nmap': 'vuln_scan',            // Nmap default should be 'vuln_scan' for vulnerability detection
      'ssl-scan': 'basic',            // SSL scan default should be 'basic'
      'mozilla-observatory': 'basic', // Mozilla Observatory default should be 'basic'
      'traffic-analysis': 'basic'     // Traffic Analysis default should be 'basic'
    }

    return scanTypeMapping[scanId] || 'basic' // Default to 'basic' for unknown tools
  }

  // Fetch URL history
  const fetchUrlHistory = useCallback(async () => {
    try {
      const response = await fetchWithRefresh('/api/scan/url-history', {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const data = await response.json()
        setUrlHistory(data.urls || [])
      }
    } catch (err) {
      console.error('Failed to fetch URL history:', err)
    }
  }, [])

  // Fetch scans function
  const fetchScans = useCallback(async (page = 1, status = 'all') => {
    try {
      setLoading(true)
      setError(null)

      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
        sort_by: 'created_at',
        sort_dir: 'desc'
      })

      if (status !== 'all') {
        // When filtering for 'completed', also include 'partially_completed' scans
        if (status === 'completed') {
          params.append('status', 'completed,partially_completed')
        } else {
          params.append('status', status)
        }
      }

      const response = await fetchWithRefresh(`/api/scan/list?${params}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (response.ok) {
        const data: ScanListResponse = await response.json()
        setScans(data.scans || [])
        setTotalScans(data.total || 0)
        setTotalPages(Math.ceil(data.total / perPage))
        setCurrentPage(data.page || 1)
      } else if (response.status === 401) {
        setError('Please log in to view your scan history')
      } else {
        setError('Failed to load scan history')
      }
    } catch (err) {
      setError('Network error - please try again')
    } finally {
      setLoading(false)
    }
  }, [perPage])

  // Helper functions for table
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge variant="default">Completed</Badge>
      case "partially_completed":
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-400">Partially Completed</Badge>
      case "running":
        return <Badge variant="secondary">Running</Badge>
      case "queued":
        return <Badge variant="outline">Queued</Badge>
      case "failed":
        return <Badge variant="destructive">Failed</Badge>
      default:
        return <Badge variant="outline">{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A'

    try {
      const date = new Date(dateString)

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date'
      }

      // Format as: "Sep 14, 2025 at 10:30 PM"
      const dateStr = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      })
      return `${dateStr} at ${timeStr}`
    } catch (error) {
      console.error('Error formatting date:', error)
      return 'Invalid Date'
    }
  }

  const formatUrlForDisplay = (url: string, scanType: string) => {
    // NMAP scans strip https:// for functionality, but we want to display it
    if (scanType.startsWith('nmap_') && !url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`
    }
    return url
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage)
    }
  }

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    fetchScans(currentPage, statusFilter)
  }

  // Update scan progress in state
  const updateScanProgress = useCallback((scanId: string, progress: number, status: string, security_score?: number, vulnerabilities_count?: number, queue_position?: number, estimated_wait_minutes?: number) => {
    // Update scan in scans list immediately
    setScans(prevScans =>
      prevScans.map(scan => {
        if (scan.id === scanId) {
          // If status changed to completed or failed, mark progress accordingly
          if (status === 'completed') {
            return {
              ...scan,
              progress: 100,
              status: 'completed' as Scan['status'],
              security_score: security_score ?? scan.security_score,
              vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count
            }
          } else if (status === 'failed') {
            return {
              ...scan,
              progress: 0,
              status: 'failed' as Scan['status'],
              security_score: security_score ?? scan.security_score,
              vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count
            }
          }
          return {
            ...scan,
            progress,
            status: status as Scan['status'],
            security_score: security_score ?? scan.security_score,
            vulnerabilities_count: vulnerabilities_count ?? scan.vulnerabilities_count,
            queue_position: queue_position,
            estimated_wait_minutes: estimated_wait_minutes
          }
        }
        return scan
      })
    )
  }, [])

  // Handle scan completion
  const handleScanComplete = useCallback((scanId: string) => {
    // Remove from active scans first to prevent duplicate handlers
    setActiveScans(prev => {
      const newSet = new Set(prev)
      const wasActive = newSet.has(scanId)
      newSet.delete(scanId)

      // Only show toast and refresh if this scan was actually being tracked
      if (wasActive) {
        // Refresh scan list to get final results - use refs to get current values
        fetchScans(currentPageRef.current, statusFilterRef.current)

        // Refresh user data to ensure scan counter is up-to-date
        checkAuthStatus()

        // Show completion toast (only once)
        toast({
          title: "Scan completed",
          description: "Your security scan has finished processing.",
        })
      }

      return newSet
    })
  }, [fetchScans, checkAuthStatus, toast])

  // Handle scan error (e.g., 404 when scan is deleted)
  const handleScanError = useCallback((scanId: string, error: string) => {
    // Remove from active scans immediately
    setActiveScans(prev => {
      const newSet = new Set(prev)
      newSet.delete(scanId)
      return newSet
    })

    // Remove from scans list immediately (for instant UI update)
    setScans(prevScans => prevScans.filter(scan => scan.id !== scanId))

    // Refresh user data to update scan limits counter
    checkAuthStatus()

    // Also refresh the scan list to ensure consistency
    fetchScans(currentPageRef.current, statusFilterRef.current)
  }, [checkAuthStatus, fetchScans])

  // Track active scans and start polling
  useEffect(() => {
    // Find all running or queued scans
    const activeScanIds = scans
      .filter(scan => scan.status === 'running' || scan.status === 'queued')
      .map(scan => scan.id)

    // Update active scans set
    setActiveScans(new Set(activeScanIds))
  }, [scans])

  // Effects
  useEffect(() => {
    fetchScans(currentPage, statusFilter)
  }, [fetchScans, currentPage, statusFilter])

  // Fetch URL history on mount
  useEffect(() => {
    fetchUrlHistory()
  }, [fetchUrlHistory])

  // Click outside handler for URL dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (urlDropdownRef.current && !urlDropdownRef.current.contains(event.target as Node)) {
        setShowUrlDropdown(false)
      }
    }

    if (showUrlDropdown) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUrlDropdown])

  const scanTypes = [
    { id: "owasp-zap", name: "OWASP ZAP", description: "Web application security scanner", endpoint: "/api/scan/zap/start" },
    { id: "nuclei", name: "Nuclei", description: "Vulnerability scanner based on templates (includes API discovery)", endpoint: "/api/scan/nuclei/start" },
    { id: "nmap", name: "Nmap", description: "Network discovery and security auditing", endpoint: "/api/scan/nmap/start" },
    { id: "ssl-scan", name: "SSL/TLS Scan", description: "SSL certificate and configuration analysis", endpoint: "/api/scan/ssl/start" },
    { id: "mozilla-observatory", name: "Mozilla Observatory", description: "HTTP security headers and configuration analysis", endpoint: "/api/scan/mozilla/start" },
    { id: "traffic-analysis", name: "Traffic Analysis", description: "Network traffic security analysis", endpoint: "/api/scan/traffic/start" },
  ]

  const handleScanToggle = (scanId: string) => {
    if (isComprehensive) return // Don't allow individual selection when comprehensive is selected
    
    setSelectedScans(prev => 
      prev.includes(scanId) 
        ? prev.filter(id => id !== scanId)
        : [...prev, scanId]
    )
  }

  const handleComprehensiveToggle = () => {
    if (!isComprehensive) {
      // Selecting comprehensive - clear individual selections
      setSelectedScans([])
      setIsComprehensive(true)
    } else {
      // Deselecting comprehensive
      setIsComprehensive(false)
    }
  }

  const handleStartScan = async () => {
    try {
      setIsSubmitting(true)
      
      if (isComprehensive) {
        // For comprehensive scan, use the single comprehensive endpoint
        try {
          const response = await fetchWithRefresh('/api/scan/create', {
            method: 'POST',
            headers: addCSRFHeader({
              'Content-Type': 'application/json'
            }),
            credentials: 'include',
            body: JSON.stringify({
              url: targetUrl,
              scan_type: 'comprehensive'
            })
          })

          // Check status first before trying to parse JSON
          if (response.status === 429) {
            // Handle concurrent limit error
            let errorData
            try {
              errorData = await response.json()
            } catch {
              errorData = { error: 'Too many concurrent scans', active_scans: 3 }
            }

            toast({
              title: "Too many concurrent scans",
              description: errorData.error || `You have ${errorData.active_scans || 3} active scans. Please wait for one to complete before starting another scan on this site.`,
              variant: "destructive",
            })
            return
          }

          if (response.ok) {
            await response.json()

            // Reset form and refresh scan list
            setTargetUrl("")
            setSelectedScans([])
            setIsComprehensive(false)

            // Refresh user data to update scan count
            await checkAuthStatus()

            // Refresh scan list to show new scan
            fetchScans(1, statusFilter)

            // Refresh URL history to include the new URL
            fetchUrlHistory()

            // Show success toast
            toast({
              title: "Scan started",
              description: "Your comprehensive security scan has been queued.",
            })
          } else {
            // Handle other errors
            let errorData
            try {
              errorData = await response.json()
            } catch {
              errorData = { error: 'An error occurred while starting the scan.' }
            }

            toast({
              title: "Failed to start scan",
              description: errorData.error || 'An error occurred while starting the scan.',
              variant: "destructive",
            })
            console.error('Failed to start comprehensive scan:', errorData.error || 'Unknown error')
          }
        } catch (error) {
          toast({
            title: "Network error",
            description: "Unable to connect to the server. Please check your internet connection.",
            variant: "destructive",
          })
          console.error('Network error starting comprehensive scan:', error)
        }
      } else {
        // For individual tool selections
        try {
          if (selectedScans.length === 1) {
            // For single tool, use the individual tool endpoint
            const scanId = selectedScans[0]
            const scan = scanTypes.find(s => s.id === scanId)

            if (scan) {
              const response = await fetchWithRefresh(scan.endpoint, {
                method: 'POST',
                headers: addCSRFHeader({
                  'Content-Type': 'application/json'
                }),
                credentials: 'include',
                body: JSON.stringify({
                  url: targetUrl,
                  scan_type: getScanTypeForTool(scanId)
                })
              })

              // Check status first before trying to parse JSON
              if (response.status === 429) {
                // Handle concurrent limit error
                let errorData
                try {
                  errorData = await response.json()
                } catch {
                  errorData = { error: 'Too many concurrent scans', active_scans: 3 }
                }

                toast({
                  title: "Too many concurrent scans",
                  description: errorData.error || `You have ${errorData.active_scans || 3} active scans. Please wait for one to complete before starting another scan on this site.`,
                  variant: "destructive",
                })
                return
              }

              if (response.ok) {
                await response.json()

                // Reset form and refresh scan list
                setTargetUrl("")
                setSelectedScans([])
                setIsComprehensive(false)

                // Refresh user data to update scan count
                await checkAuthStatus()

                // Refresh scan list to show new scan
                fetchScans(1, statusFilter)

                // Refresh URL history to include the new URL
                fetchUrlHistory()

                // Show success toast
                toast({
                  title: "Scan started",
                  description: `Your ${scan.name} scan has been queued.`,
                })
              } else {
                // Handle other errors
                let errorData
                try {
                  errorData = await response.json()
                } catch {
                  errorData = { error: 'An error occurred while starting the scan.' }
                }

                toast({
                  title: "Failed to start scan",
                  description: errorData.error || 'An error occurred while starting the scan.',
                  variant: "destructive",
                })
                console.error('Failed to start scan:', errorData.error || 'Unknown error')
              }
            }
          } else {
            // For multiple tools, use the multi scan type
            const toolMap: Record<string, string> = {
              'owasp-zap': 'zap',
              'nuclei': 'nuclei',
              'nmap': 'nmap',
              'ssl-scan': 'ssl',
              'mozilla-observatory': 'mozilla_observatory',
              'traffic-analysis': 'traffic_analysis',
            }

            const tools = selectedScans.map(scanId => toolMap[scanId]).filter(Boolean)

            const response = await fetchWithRefresh('/api/scan/create', {
              method: 'POST',
              headers: addCSRFHeader({
                'Content-Type': 'application/json'
              }),
              credentials: 'include',
              body: JSON.stringify({
                url: targetUrl,
                scan_type: 'multi',
                tools: tools
              })
            })

            // Check status first before trying to parse JSON
            if (response.status === 429) {
              // Handle concurrent limit error
              let errorData
              try {
                errorData = await response.json()
              } catch {
                errorData = { error: 'Too many concurrent scans', active_scans: 3 }
              }

              toast({
                title: "Too many concurrent scans",
                description: errorData.error || `You have ${errorData.active_scans || 3} active scans. Please wait for one to complete before starting another scan on this site.`,
                variant: "destructive",
              })
              return
            }

            if (response.ok) {
              await response.json()

              // Reset form and refresh scan list
              setTargetUrl("")
              setSelectedScans([])
              setIsComprehensive(false)

              // Refresh user data to update scan count
              await checkAuthStatus()

              // Refresh scan list to show new scan
              fetchScans(1, statusFilter)

              // Refresh URL history to include the new URL
              fetchUrlHistory()

              // Show success toast
              toast({
                title: "Scans started",
                description: `Your ${tools.length} security scans have been queued.`,
              })
            } else {
              // Handle other errors
              let errorData
              try {
                errorData = await response.json()
              } catch {
                errorData = { error: 'An error occurred while starting the scans.' }
              }

              toast({
                title: "Failed to start scans",
                description: errorData.error || 'An error occurred while starting the scans.',
                variant: "destructive",
              })
              console.error('Failed to start multi scan:', errorData.error || 'Unknown error')
            }
          }
        } catch (error) {
          toast({
            title: "Network error",
            description: "Unable to connect to the server. Please check your internet connection.",
            variant: "destructive",
          })
          console.error('Network error starting scan:', error)
        }
      }

    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      })
      console.error('Error starting scans:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const canStartScan = targetUrl && (isComprehensive || selectedScans.length > 0) && !isSubmitting

  // Check if user can run scan based on tier
  // Only calculate these values if user data is loaded (not loading and user exists)
  const scansRemaining = user?.scans_remaining_today ?? 0
  const scansPerDay = user?.scans_per_day ?? 0
  const tier = user?.subscription_tier ?? 'none'
  // Don't block scans while auth is loading
  const canRunScan = authLoading ? true : (user ? scansRemaining > 0 : false)

  // Get upgrade message based on tier
  const getUpgradeMessage = () => {
    // Don't show any message if auth is still loading
    if (authLoading || !user) {
      return null
    }

    if (tier === 'none') {
      return {
        title: "No Scans Available",
        message: "Free tier does not include scan access. Upgrade to Pro to run scans.",
        showUpgrade: true
      }
    }

    if (!canRunScan) {
      if (tier === 'pro') {
        return {
          title: "Daily Scan Limit Reached",
          message: `You've used all ${scansPerDay} scans today. Limit resets in ${resetTimer.hours}h ${resetTimer.minutes}m.`,
          showUpgrade: false
        }
      }
      // Trial tier
      return {
        title: "Daily Scan Limit Reached",
        message: `You've used all ${scansPerDay} scans today. Upgrade to Pro for 50 scans/day or wait ${resetTimer.hours}h ${resetTimer.minutes}m for reset.`,
        showUpgrade: true
      }
    }

    return null
  }

  const upgradeMessage = getUpgradeMessage()

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Helmet>
        <title>Scans | Fusegu Security</title>
        <meta name="description" content="Manage and configure security scans for your applications." />
      </Helmet>
      <Navigation />
      <ToastContainer toasts={toasts} onDismiss={removeToast} />

      <main className="flex flex-1 flex-col gap-4 px-4 pb-4 md:gap-8 md:px-8 md:pb-8 pt-20">
        <div className="mx-auto grid w-full max-w-6xl gap-6">
          <div className="grid gap-6">
            <Card>
              <CardHeader>
                <CardTitle>New Security Scan</CardTitle>
                <CardDescription>
                  Configure and launch security scans for your applications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Upgrade prompt banner */}
                {upgradeMessage && (
                  <div className={`rounded-lg border p-4 ${
                    tier === 'none' ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'
                  }`}>
                    <div className="flex gap-3">
                      {tier === 'none' ? (
                        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          tier === 'none' ? 'text-amber-900' : 'text-blue-900'
                        }`}>
                          {upgradeMessage.title}
                        </p>
                        <p className={`mt-1 text-sm ${
                          tier === 'none' ? 'text-amber-700' : 'text-blue-700'
                        }`}>
                          {upgradeMessage.message}
                        </p>
                        {upgradeMessage.showUpgrade && (
                          <Link to="/upgrade">
                            <Button size="sm" className="mt-3" variant={tier === 'none' ? 'default' : 'outline'}>
                              <Zap className="h-4 w-4 mr-2" />
                              {'Upgrade to Pro'}
                            </Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {/* Propagation delay info banner */}
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex gap-3">
                    <svg className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">
                        Note about scan results
                      </p>
                      <p className="mt-1 text-sm text-blue-700">
                        Site changes (security headers, SSL certificates, DNS updates) can take a few minutes to hours to propagate and reflect in scan results. If you've recently made changes, you may need to wait before rescanning.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="target-url">Target URL</Label>
                  <div className="relative" ref={urlDropdownRef}>
                    <Input
                      id="target-url"
                      placeholder="https://example.com"
                      value={targetUrl}
                      onChange={(e) => setTargetUrl(e.target.value)}
                      className="pr-10"
                    />
                    {urlHistory.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setShowUrlDropdown(!showUrlDropdown)
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                        title="Show URL history"
                      >
                        <History className="h-4 w-4 text-gray-500" />
                      </button>
                    )}

                    {/* URL History Dropdown */}
                    {showUrlDropdown && urlHistory.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                        <div className="py-1">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                            Recent URLs
                          </div>
                          {urlHistory.map((item, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => {
                                setTargetUrl(item.url)
                                setShowUrlDropdown(false)
                              }}
                              className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {item.url}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Scanned {item.scan_count} time{item.scan_count !== 1 ? 's' : ''}
                                    {item.last_scanned && ` • Last: ${new Date(item.last_scanned).toLocaleDateString()}`}
                                  </p>
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <Label className="text-base font-semibold">Scan Configuration</Label>
                  
                  {/* Comprehensive Scan Option */}
                  <Card className={`transition-all ${isComprehensive ? 'ring-2 ring-primary shadow-lg' : ''}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center space-x-3">
                        <Checkbox
                          id="comprehensive"
                          checked={isComprehensive}
                          onCheckedChange={handleComprehensiveToggle}
                        />
                        <div className="space-y-1">
                          <Label 
                            htmlFor="comprehensive" 
                            className="text-base font-semibold cursor-pointer flex items-center gap-2"
                          >
                            Comprehensive Security Scan
                            <Badge variant="secondary" className="bg-primary/10 text-primary">
                              Recommended
                            </Badge>
                          </Label>
                          <p className="text-sm text-muted-foreground">
                            Run all available security tools in one comprehensive scan. Includes OWASP ZAP, Nuclei, Nmap, SSL/TLS analysis, web crawling, code analysis (JavaScript), secrets detection, NPM audit (package.json), security headers, and more. The crawler extracts JavaScript and package.json files which are then analyzed for security issues.
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    {isComprehensive && (
                      <CardContent className="pt-0">
                        <div className="text-sm text-muted-foreground">
                          ✓ Complete security assessment with 10+ security tools<br/>
                          ✓ Automated vulnerability detection and classification<br/>
                          ✓ Detailed reporting with remediation recommendations<br/>
                          ✓ Estimated completion time: 15-30 minutes
                        </div>
                      </CardContent>
                    )}
                  </Card>

                  {/* Individual Scan Options */}
                  <div className={`space-y-3 transition-opacity ${isComprehensive ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Label className="text-sm font-medium text-muted-foreground">
                      Or select individual scans:
                    </Label>
                    
                    <div className="grid gap-3 md:grid-cols-2">
                      {scanTypes.map((scan) => (
                        <Card 
                          key={scan.id} 
                          className={`transition-all cursor-pointer ${
                            selectedScans.includes(scan.id) && !isComprehensive ? 'ring-1 ring-primary' : ''
                          }`}
                          onClick={() => handleScanToggle(scan.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                checked={selectedScans.includes(scan.id)}
                                disabled={isComprehensive}
                                className="mt-0.5"
                              />
                              <div className="space-y-1 flex-1">
                                <Label className="text-sm font-medium cursor-pointer">
                                  {scan.name}
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                  {scan.description}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {isComprehensive
                      ? "Comprehensive scan selected (all tools included)"
                      : `${selectedScans.length} individual scan(s) selected`
                    }
                    {canRunScan && scansRemaining > 0 && scansRemaining <= 5 && (
                      <div className="mt-1 text-xs text-amber-600">
                        {scansRemaining} scan{scansRemaining !== 1 ? 's' : ''} remaining today
                      </div>
                    )}
                  </div>
                  <Button
                    onClick={handleStartScan}
                    disabled={!canStartScan || !canRunScan}
                    className="min-w-32"
                  >
                    {isSubmitting ? "Starting..." : !canRunScan ? "Limit Reached" : "Start Scan"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Scan History Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Scan History</CardTitle>
                    <CardDescription>
                      {totalScans} total scans
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={handleStatusFilter}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="running">Running</SelectItem>
                        <SelectItem value="queued">Queued</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleRefresh}
                      variant="outline"
                      size="sm"
                      disabled={loading}
                    >
                      <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading && scans.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-center">
                      <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p>Loading scan history...</p>
                    </div>
                  </div>
                ) : error ? (
                  <div className="text-center py-12">
                    <div className="text-center">
                      <p className="text-red-600 mb-4">{error}</p>
                      <Button onClick={handleRefresh} variant="outline">
                        Try Again
                      </Button>
                    </div>
                  </div>
                ) : scans.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No scans found</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {statusFilter !== 'all' && 'Try changing the status filter or '}
                      Start your first security scan to see results here.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Mobile: Card view */}
                    <div className="md:hidden space-y-4">
                      {scans.map((scan) => {
                        const progress = scan.progress ?? 0
                        const isActive = scan.status === 'running' || scan.status === 'queued'

                        return (
                          <Card key={scan.id} className="p-4">
                            <div className="space-y-3">
                              {/* URL */}
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Target URL</p>
                                <p className="text-sm font-medium break-all">
                                  {formatUrlForDisplay(scan.url, scan.scan_type)}
                                </p>
                              </div>

                              {/* Scan Type & Status */}
                              <div className="flex items-center justify-between gap-2">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Scan Type</p>
                                  <p className="text-sm">{getScanTypeDisplayName(scan.scan_type)}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                                  <div className="flex items-center gap-2">
                                    {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {getStatusBadge(scan.status)}
                                  </div>
                                </div>
                              </div>

                              {/* Progress */}
                              {(scan.status === 'queued' || isActive) && (
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Progress</p>
                                  {scan.status === 'queued' && scan.queue_position !== undefined ? (
                                    <div className="space-y-1">
                                      <p className="text-sm text-muted-foreground">
                                        Position: {scan.queue_position + 1}
                                      </p>
                                      {scan.estimated_wait_minutes !== undefined && (
                                        <p className="text-xs text-muted-foreground">
                                          {scan.estimated_wait_minutes > 0
                                            ? `~${scan.estimated_wait_minutes} min wait`
                                            : 'Starting soon'}
                                        </p>
                                      )}
                                    </div>
                                  ) : isActive ? (
                                    <div className="space-y-1">
                                      <Progress value={progress} className="h-2" />
                                      <p className="text-xs text-muted-foreground">{progress}%</p>
                                    </div>
                                  ) : null}
                                </div>
                              )}

                              {/* Date */}
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Created</p>
                                <p className="text-sm">{formatDate(scan.created_at)}</p>
                              </div>

                              {/* Actions */}
                              <Button
                                className="w-full"
                                variant="outline"
                                onClick={() => handleViewReport(scan.id)}
                                disabled={scan.status !== 'completed' && scan.status !== 'partially_completed'}
                              >
                                View Results
                              </Button>
                            </div>
                          </Card>
                        )
                      })}
                    </div>

                    {/* Desktop: Table view */}
                    <div className="hidden md:block rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>URL</TableHead>
                            <TableHead>Scan Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Progress</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {scans.map((scan) => {
                            const progress = scan.progress ?? 0
                            const isActive = scan.status === 'running' || scan.status === 'queued'

                            return (
                              <TableRow key={scan.id}>
                                <TableCell>
                                  <div className="max-w-xs truncate" title={formatUrlForDisplay(scan.url, scan.scan_type)}>
                                    {formatUrlForDisplay(scan.url, scan.scan_type)}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {getScanTypeDisplayName(scan.scan_type)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {isActive && <Loader2 className="h-3 w-3 animate-spin" />}
                                    {getStatusBadge(scan.status)}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {scan.status === 'queued' && scan.queue_position !== undefined ? (
                                    <div className="space-y-1">
                                      <p className="text-sm text-muted-foreground">
                                        Position: {scan.queue_position + 1}
                                      </p>
                                      {scan.estimated_wait_minutes !== undefined && (
                                        <p className="text-xs text-muted-foreground">
                                          {scan.estimated_wait_minutes > 0
                                            ? `~${scan.estimated_wait_minutes} min wait`
                                            : 'Starting soon'}
                                        </p>
                                      )}
                                    </div>
                                  ) : isActive ? (
                                    <div className="space-y-1">
                                      <Progress value={progress} className="h-2 w-32" />
                                      <p className="text-xs text-muted-foreground">{progress}%</p>
                                    </div>
                                  ) : scan.status === 'completed' || scan.status === 'partially_completed' ? (
                                    <span className="text-sm text-muted-foreground">Complete</span>
                                  ) : scan.status === 'failed' ? (
                                    <span className="text-sm text-destructive">Failed</span>
                                  ) : (
                                    <span className="text-sm text-muted-foreground">-</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {formatDate(scan.created_at)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewReport(scan.id)}
                                    disabled={scan.status !== 'completed' && scan.status !== 'partially_completed'}
                                  >
                                    View Results
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalScans)} of {totalScans} scans
                        </p>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage <= 1}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Previous</span>
                          </Button>
                          <span className="text-xs sm:text-sm px-2">
                            {currentPage} / {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage >= totalPages}
                          >
                            <span className="hidden sm:inline">Next</span>
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Report Preview Modal */}
      {selectedReport && reportData && (
        <ReportPreview
          reportData={reportData}
          onClose={handleClosePreview}
          onDownload={() => handleDownloadReport(selectedReport)}
        />
      )}

      {/* Scan Progress Trackers - invisible components that poll active scans */}
      {Array.from(activeScans).map(scanId => (
        <ScanProgressTracker
          key={scanId}
          scanId={scanId}
          onUpdate={updateScanProgress}
          onComplete={handleScanComplete}
          onError={handleScanError}
          toast={toast}
        />
      ))}
    </div>
  )
}